// Centralized operator configuration for all filter components
// This eliminates code duplication and provides a single source of truth

export interface ExtendedOperator {
  value: string;
  label: string;
  requiresValue: boolean;
  supportedFieldTypes?: string[];
}

// Core operator definitions
export const OPERATORS = {
  // Equality operators
  EQ: { value: 'eq', label: 'equals', requiresValue: true },
  NE: { value: 'ne', label: 'not equals', requiresValue: true },

  // Field comparison operators
  EQ_FIELD: { value: 'eq_field', label: 'equals (field)', requiresValue: true },
  NE_FIELD: { value: 'ne_field', label: 'not equals (field)', requiresValue: true },

  // Cross-entity field comparison operators (compares target field with source field)
  EQ_SOURCE_FIELD: { value: 'eq_source_field', label: 'equals (source field)', requiresValue: true },
  NE_SOURCE_FIELD: { value: 'ne_source_field', label: 'not equals (source field)', requiresValue: true },

  // String operators
  CONTAINS: { value: 'contains', label: 'contains', requiresValue: true },
  NOT_CONTAINS: { value: 'notcontains', label: 'not contains', requiresValue: true },
  STARTS_WITH: { value: 'startswith', label: 'starts with', requiresValue: true },
  ENDS_WITH: { value: 'endswith', label: 'ends with', requiresValue: true },

  // Numeric/Date comparison operators
  GT: { value: 'gt', label: 'greater than', requiresValue: true },
  GE: { value: 'ge', label: 'greater than or equal', requiresValue: true },
  GTE: { value: 'gte', label: 'greater than or equal', requiresValue: true },
  LT: { value: 'lt', label: 'less than', requiresValue: true },
  LE: { value: 'le', label: 'less than or equal', requiresValue: true },
  LTE: { value: 'lte', label: 'less than or equal', requiresValue: true },

  // List operators
  IN: { value: 'in', label: 'in', requiresValue: true },
  NOT_IN: { value: 'notin', label: 'not in', requiresValue: true },

  // Null operators
  NULL: { value: 'null', label: 'is null', requiresValue: false },
  NOT_NULL: { value: 'notnull', label: 'is not null', requiresValue: false },

  // Data presence operators
  CONTAINS_DATA: { value: 'containsdata', label: 'contains data', requiresValue: false },
  DOES_NOT_CONTAIN_DATA: { value: 'doesnotcontaindata', label: 'does not contain data', requiresValue: false },
} as const;

// Field type specific operator mappings
export const OPERATORS_BY_FIELD_TYPE = {
  string: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    OPERATORS.EQ_SOURCE_FIELD,
    OPERATORS.NE_SOURCE_FIELD,
    OPERATORS.CONTAINS,
    OPERATORS.NOT_CONTAINS,
    OPERATORS.STARTS_WITH,
    OPERATORS.ENDS_WITH,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  memo: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    OPERATORS.CONTAINS,
    OPERATORS.NOT_CONTAINS,
    OPERATORS.STARTS_WITH,
    OPERATORS.ENDS_WITH,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  uniqueidentifier: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  number: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    OPERATORS.GT,
    OPERATORS.LT,
    OPERATORS.GTE,
    OPERATORS.LTE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  integer: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    OPERATORS.GT,
    OPERATORS.LT,
    OPERATORS.GTE,
    OPERATORS.LTE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  decimal: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    OPERATORS.GT,
    OPERATORS.LT,
    OPERATORS.GTE,
    OPERATORS.LTE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  double: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    OPERATORS.GT,
    OPERATORS.LT,
    OPERATORS.GTE,
    OPERATORS.LTE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  money: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    OPERATORS.GT,
    OPERATORS.LT,
    OPERATORS.GTE,
    OPERATORS.LTE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  bigint: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    OPERATORS.GT,
    OPERATORS.LT,
    OPERATORS.GTE,
    OPERATORS.LTE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  boolean: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  datetime: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    { ...OPERATORS.GT, label: 'after' },
    { ...OPERATORS.LT, label: 'before' },
    { ...OPERATORS.GTE, label: 'on or after' },
    { ...OPERATORS.LTE, label: 'on or before' },
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  date: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.EQ_FIELD,
    OPERATORS.NE_FIELD,
    { ...OPERATORS.GT, label: 'after' },
    { ...OPERATORS.LT, label: 'before' },
    { ...OPERATORS.GTE, label: 'on or after' },
    { ...OPERATORS.LTE, label: 'on or before' },
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  picklist: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  state: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  status: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  optionset: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  choice: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  multiselectpicklist: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.IN,
    OPERATORS.NOT_IN,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  lookup: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  customer: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],

  owner: [
    OPERATORS.EQ,
    OPERATORS.NE,
    OPERATORS.NULL,
    OPERATORS.NOT_NULL,
  ],
} as const;

/**
 * Get valid operators for a specific field type
 * @param fieldType - The Dataverse field type (AttributeType)
 * @param options - Optional configuration
 * @param options.excludeCrossEntityOperators - Whether to exclude cross-entity field comparison operators (default: false)
 * @returns Array of valid operators for the field type
 */
export function getOperatorsForFieldType(
  fieldType?: string,
  options?: { excludeCrossEntityOperators?: boolean }
): ExtendedOperator[] {
  const excludeCrossEntity = options?.excludeCrossEntityOperators ?? false;

  if (!fieldType) {
    // Return all operators as fallback when no field type is specified
    const allOps = Object.values(OPERATORS);
    if (excludeCrossEntity) {
      return allOps.filter(op =>
        op.value !== 'eq_source_field' && op.value !== 'ne_source_field'
      );
    }
    return allOps;
  }

  const type = fieldType.toLowerCase();
  const operatorsForType = OPERATORS_BY_FIELD_TYPE[type as keyof typeof OPERATORS_BY_FIELD_TYPE];

  let operators: ExtendedOperator[];
  if (operatorsForType) {
    operators = [...operatorsForType];
  } else {
    // Fallback to string operators for unknown types
    operators = [...OPERATORS_BY_FIELD_TYPE.string];
  }

  // Filter out cross-entity operators if requested
  if (excludeCrossEntity) {
    operators = operators.filter(op =>
      op.value !== 'eq_source_field' && op.value !== 'ne_source_field'
    );
  }

  return operators;
}

/**
 * Check if an operator requires a value
 * @param operator - The operator value
 * @returns Whether the operator requires a value input
 */
export function operatorRequiresValue(operator: string): boolean {
  const operatorObj = Object.values(OPERATORS).find(op => op.value === operator);
  return operatorObj?.requiresValue ?? true;
}

// Export all operators as a flat array for compatibility with existing code
export const DEFAULT_OPERATORS: ExtendedOperator[] = [
  // Include supportedFieldTypes for backward compatibility with field-providers.ts
  { ...OPERATORS.EQ, supportedFieldTypes: ['string', 'number', 'boolean', 'lookup'] },
  { ...OPERATORS.NE, supportedFieldTypes: ['string', 'number', 'boolean', 'lookup'] },
  { ...OPERATORS.CONTAINS, supportedFieldTypes: ['string'] },
  { ...OPERATORS.NOT_CONTAINS, supportedFieldTypes: ['string'] },
  { ...OPERATORS.STARTS_WITH, supportedFieldTypes: ['string'] },
  { ...OPERATORS.ENDS_WITH, supportedFieldTypes: ['string'] },
  { ...OPERATORS.GT, supportedFieldTypes: ['number', 'datetime'] },
  { ...OPERATORS.GE, supportedFieldTypes: ['number', 'datetime'] },
  { ...OPERATORS.LT, supportedFieldTypes: ['number', 'datetime'] },
  { ...OPERATORS.LE, supportedFieldTypes: ['number', 'datetime'] },
  { ...OPERATORS.NULL, supportedFieldTypes: ['string', 'number', 'lookup'] },
  { ...OPERATORS.NOT_NULL, supportedFieldTypes: ['string', 'number', 'lookup'] },
  { ...OPERATORS.IN, supportedFieldTypes: ['string', 'number', 'lookup'] },
  { ...OPERATORS.NOT_IN, supportedFieldTypes: ['string', 'number', 'lookup'] }
];

// Export for testing/mock purposes - simplified operator list
export const MOCK_OPERATORS = [
  OPERATORS.EQ,
  OPERATORS.NE,
  OPERATORS.GT,
  OPERATORS.LT,
  OPERATORS.GTE,
  OPERATORS.LTE,
  OPERATORS.CONTAINS,
  OPERATORS.NOT_CONTAINS,
  OPERATORS.STARTS_WITH,
  OPERATORS.ENDS_WITH,
  OPERATORS.IN,
  OPERATORS.NOT_IN,
  OPERATORS.NULL,
  OPERATORS.NOT_NULL,
];